import React, { useState, useContext, useEffect } from 'react';
import { 
  Box, 
  Heading, 
  Text, 
  VStack, 
  HStack, 
  Flex, 
  Tag, 
  TagLabel, 
  IconButton, 
  Spinner, 
  Grid, 
  GridItem, 
  Card, 
  CardHeader, 
  CardBody,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody
} from '@chakra-ui/react';
import { ViewIcon, DownloadIcon, AttachmentIcon } from '@chakra-ui/icons';
import { callApi } from '../../callApi';
import { AppContext } from '../AppContext';
import { useToast, Button } from '@chakra-ui/react';

const DocumentManagerPanel = ({ caseId, caseFiles, onUploadSuccess }) => {
  const { accessToken } = useContext(AppContext);
  const toast = useToast();
  const [isRecoding, setIsRecoding] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState('');
  const [docUrl, setDocUrl] = useState(null);
  const [isDocLoading, setIsDocLoading] = useState(false);
  const [selectedTextFile, setSelectedTextFile] = useState(null);
  const [isTextModalOpen, setIsTextModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    return () => {
      if (docUrl) {
        window.URL.revokeObjectURL(docUrl);
      }
    };
  }, [docUrl]);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const handleFileUpload = async () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      setIsUploading(true);
      try {
        const formData = new FormData();
        formData.append('file_type_id', '5'); // Default ID to satisfy DB constraint
        formData.append('file', file);

        const response = await callApi(`/upload_file/${caseId}`, accessToken, {
          method: 'POST',
          body: formData,
        });

        const data = await response.json();
        if (response.ok) {
          toast({
            title: 'File uploaded',
            description: 'Your document was successfully uploaded',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          if (onUploadSuccess) {
            onUploadSuccess();
          }
        } else {
          throw new Error(data.error || 'Failed to upload file');
        }
      } catch (error) {
        toast({
          title: 'File upload failed',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsUploading(false);
      }
    };
    fileInput.click();
  };

  const handleFileSelect = async (fileId) => {
    setSelectedFileId(fileId);
    setIsDocLoading(true);
    
    try {
      const response = await callApi('/download_case_file', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ case_id: caseId, file_id: fileId }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        if (docUrl) window.URL.revokeObjectURL(docUrl);
        setDocUrl(url);
      } else {
        console.error('Failed to load document');
      }
    } catch (error) {
      console.error('Error loading document:', error);
    } finally {
      setIsDocLoading(false);
    }
  };

  const handleFileDownload = async (file) => {
    try {
      const response = await callApi('/download_case_file', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ case_id: caseId, file_id: file.id }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = file.file_name;
        document.body.appendChild(a);
        a.click();
        
        setTimeout(() => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 100);
      }
    } catch (error) {
      console.error('Download failed', error);
    }
  };

  return (
    <Box mt={8}>
      <Card>
        <CardHeader pb={0}>
          <Flex justify="space-between" align="center">
            <Heading size="md">Source Documents</Heading>
            <Button 
              size="sm" 
              colorScheme="blue" 
              isLoading={isRecoding}
              loadingText="Queueing..."
              onClick={async () => {
                setIsRecoding(true);
                try {
                  const response = await callApi('/api/jobs', accessToken, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      job_type: 'recode',
                      payload: {
                        case_id: caseId,
                        external_case_id: caseId
                      }
                    })
                  });
                  if (response.ok) {
                    toast({
                      title: 'Job Queued',
                      description: 'Recode job has been successfully queued.',
                      status: 'success',
                      duration: 3000,
                      isClosable: true,
                    });
                  } else {
                    throw new Error('Failed to queue job');
                  }
                } catch (error) {
                  toast({
                    title: 'Error',
                    description: 'Failed to queue recode job.',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                  });
                } finally {
                  setTimeout(() => setIsRecoding(false), 2000);
                }
              }}
            >
              Re-code Case
            </Button>
          </Flex>
        </CardHeader>
        <CardBody>
          <Grid templateColumns="300px 1fr" gap={6} h="600px">
            {/* Left Side: File List */}
            <GridItem overflowY="auto" pr={2}>
              <Box mb={4}>
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontSize="sm" fontWeight="bold" color="gray.600" textTransform="uppercase">
                    Clinical Documents
                  </Text>
                  <Button
                    size="xs"
                    leftIcon={<AttachmentIcon />}
                    colorScheme="blue"
                    variant="outline"
                    onClick={handleFileUpload}
                    isLoading={isUploading}
                  >
                    Upload
                  </Button>
                </Flex>
                <VStack align="stretch" spacing={2}>
                  {caseFiles.filter(f => f.document_category !== 'PayerResponse').length > 0 ? (
                    caseFiles.filter(f => f.document_category !== 'PayerResponse').map(file => (
                      <Box
                        key={file.id}
                        p={3}
                        cursor="pointer"
                        borderRadius="md"
                        bg={selectedFileId === file.id ? "blue.50" : "transparent"}
                        _hover={{ bg: "gray.50" }}
                        borderWidth="1px"
                        borderColor={selectedFileId === file.id ? "blue.200" : "transparent"}
                        onClick={() => handleFileSelect(file.id)}
                      >
                        <Flex justify="space-between" align="start">
                          <VStack align="start" spacing={1} flex="1">
                            <Text fontSize="sm" fontWeight="medium" noOfLines={2} title={file.file_name}>
                              {file.file_name}
                            </Text>
                            <HStack>
                              <Tag size="sm" colorScheme="blue" variant="subtle">
                                <TagLabel fontSize="xs">{file.file_type}</TagLabel>
                              </Tag>
                              <Text fontSize="xs" color="gray.500">
                                {formatFileSize(file.file_size)}
                              </Text>
                            </HStack>
                          </VStack>
                          <HStack spacing={1}>
                            <IconButton
                              icon={<ViewIcon />}
                              size="xs"
                              variant="ghost"
                              colorScheme="blue"
                              aria-label="View file text"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTextFile(file);
                                setIsTextModalOpen(true);
                              }}
                            />
                            <IconButton
                              icon={<DownloadIcon />}
                              size="xs"
                              variant="ghost"
                              colorScheme="blue"
                              aria-label="Download file"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFileDownload(file);
                              }}
                            />
                          </HStack>
                        </Flex>
                      </Box>
                    ))
                  ) : (
                    <Text fontSize="sm" color="gray.500" fontStyle="italic">No clinical documents found.</Text>
                  )}
                </VStack>
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={2} textTransform="uppercase">
                  Payer Response
                </Text>
                <VStack align="stretch" spacing={2}>
                  {caseFiles.filter(f => f.document_category === 'PayerResponse').length > 0 ? (
                    caseFiles.filter(f => f.document_category === 'PayerResponse').map(file => (
                      <Box
                        key={file.id}
                        p={3}
                        cursor="pointer"
                        borderRadius="md"
                        bg={selectedFileId === file.id ? "blue.50" : "transparent"}
                        _hover={{ bg: "gray.50" }}
                        borderWidth="1px"
                        borderColor={selectedFileId === file.id ? "blue.200" : "transparent"}
                        onClick={() => handleFileSelect(file.id)}
                      >
                        <Flex justify="space-between" align="start">
                          <VStack align="start" spacing={1} flex="1">
                            <Text fontSize="sm" fontWeight="medium" noOfLines={2} title={file.file_name}>
                              {file.file_name}
                            </Text>
                            <HStack>
                              <Tag size="sm" colorScheme="blue" variant="subtle">
                                <TagLabel fontSize="xs">{file.file_type}</TagLabel>
                              </Tag>
                              <Text fontSize="xs" color="gray.500">
                                {formatFileSize(file.file_size)}
                              </Text>
                            </HStack>
                          </VStack>
                          <HStack spacing={1}>
                            <IconButton
                              icon={<ViewIcon />}
                              size="xs"
                              variant="ghost"
                              colorScheme="blue"
                              aria-label="View file text"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTextFile(file);
                                setIsTextModalOpen(true);
                              }}
                            />
                            <IconButton
                              icon={<DownloadIcon />}
                              size="xs"
                              variant="ghost"
                              colorScheme="blue"
                              aria-label="Download file"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFileDownload(file);
                              }}
                            />
                          </HStack>
                        </Flex>
                      </Box>
                    ))
                  ) : (
                    <Text fontSize="sm" color="gray.500" fontStyle="italic">No payer response documents found.</Text>
                  )}
                </VStack>
              </Box>
            </GridItem>

            {/* Right Side: Document Viewer */}
            <GridItem position="relative" bg="gray.100" h="100%">
              {isDocLoading ? (
                <Flex justify="center" align="center" h="100%">
                  <VStack>
                    <Spinner size="xl" color="blue.500" />
                    <Text>Loading Document...</Text>
                  </VStack>
                </Flex>
              ) : docUrl ? (
                <iframe 
                  src={docUrl} 
                  title="Document Viewer"
                  width="100%" 
                  height="100%" 
                  style={{ border: 'none' }} 
                />
              ) : (
                <Flex justify="center" align="center" h="100%" color="gray.500" flexDirection="column" p={6} textAlign="center">
                  <Text fontSize="lg" mb={2}>No Document Selected</Text>
                  <Text fontSize="sm">Click a document from the list on the left to view it.</Text>
                </Flex>
              )}
            </GridItem>
          </Grid>
        </CardBody>
      </Card>

      {/* Text Content Modal */}
      <Modal isOpen={isTextModalOpen} onClose={() => setIsTextModalOpen(false)} size="4xl">
        <ModalOverlay />
        <ModalContent maxH="80vh">
          <ModalHeader>{selectedTextFile?.file_name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto" pb={6}>
            <Box bg="gray.50" p={4} borderRadius="md" whiteSpace="pre-wrap" fontFamily="monospace" fontSize="sm">
              {selectedTextFile?.file_text || "No text content available for this file."}
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default DocumentManagerPanel;
